import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Branch, Contact, NewContact } from "@/api/queries";
import { useCreateContact, useUpdateContact } from "@/api/queries";
import { Dialog } from "@/components/Dialog";
import { Button, FormField, Input, Select, Seg } from "@/components/ui";
import type { EmailRow, PhoneRow } from "./ContactChannels";
import {
  EmailList,
  PhoneList,
  emailRows,
  emailsBody,
  phoneRows,
  phonesBody,
} from "./ContactChannels";

/**
 * Screen 07 of the Modernist design — *Agregar contacto* (.docs/08 §7 F3).
 *
 * Half this modal is compliance, and it is the half that decides whether the
 * CRM can legally call the person it just recorded. Two rules run through it:
 *
 * - **"Sin respuesta ≠ negativa."** The consents are tri-state because
 *   `Contact.consents_to_*` are `BooleanField(null=True)`, and `null` — never
 *   asked — is a different fact from "they said no". The default is *No se
 *   preguntó* and leaving it there writes nothing at all.
 * - **A flag is never a field write.** The answers go up as a `consents`
 *   block, and the server hands each one to `services.consent.record`, which
 *   writes the `ConsentEvent` beside the flag in one transaction (.docs/06
 *   §2.1). The flags themselves stay read-only, as they have since P6.
 *
 * Editing an existing contact deliberately does **not** re-offer the consents:
 * see the note on `isEdit` below.
 */

const DATA_SOURCES = ["1", "2", "3", "4", "7"] as const;
const NOTICE_VERSIONS = ["2026.1", "2024.2", "2021.1"] as const;

type Answer = "yes" | "no" | "unasked";

const ANSWER_TO_BOOLEAN: Record<Answer, boolean | null> = {
  yes: true,
  no: false,
  unasked: null,
};

const CONSENTS = [
  { key: "calls", label: "consentCalls", note: "consentCallsNote" },
  { key: "recording", label: "consentRecording", note: "consentRecordingNote" },
  { key: "whatsapp", label: "consentWhatsapp", note: "consentWhatsappNote" },
  { key: "marketing", label: "consentMarketing", note: "consentMarketingNote" },
] as const;

type ConsentKey = (typeof CONSENTS)[number]["key"];

type Draft = {
  name: string;
  job_title: string;
  branch: string;
  is_decision_maker: boolean;
  data_source: string;
  notice_date: string;
  notice_time: string;
  notice_version: string;
  objected_at: string;
  blocked_at: string;
  consents: Record<ConsentKey, Answer>;
  phones: PhoneRow[];
  emails: EmailRow[];
};

/**
 * What a new contact's form opens with, where the host already knows it: the
 * number just dialled, or everything reception heard on an inbound call.
 */
type Seed = {
  name: string | undefined;
  phone: string | undefined;
  email: string | undefined;
  dataSource: string | undefined;
};

const NO_SEED: Seed = {
  name: undefined,
  phone: undefined,
  email: undefined,
  dataSource: undefined,
};

function emptyDraft(
  branches: Branch[],
  contact?: Contact,
  seed: Seed = NO_SEED,
): Draft {
  const phones = phoneRows(contact);
  const [firstPhone, ...otherPhones] = phones;
  const emails = emailRows(contact);
  const [firstEmail, ...otherEmails] = emails;
  return {
    name: contact?.name ?? seed.name ?? "",
    job_title: contact?.job_title ?? "",
    branch: String(contact?.branch ?? branches[0]?.id ?? ""),
    is_decision_maker: contact?.is_decision_maker ?? false,
    data_source: contact?.data_source
      ? String(contact.data_source)
      : (seed.dataSource ?? ""),
    notice_date: contact?.privacy_notice_delivered_at?.slice(0, 10) ?? "",
    notice_time: contact?.privacy_notice_delivered_at?.slice(11, 16) ?? "",
    notice_version: contact?.privacy_notice_version ?? "",
    objected_at: contact?.objected_at?.slice(0, 10) ?? "",
    blocked_at: contact?.blocked_at?.slice(0, 10) ?? "",
    consents: {
      calls: "unasked",
      recording: "unasked",
      whatsapp: "unasked",
      marketing: "unasked",
    },
    // A new contact met on a call already has one number: the one dialled.
    phones:
      firstPhone && !contact && seed.phone
        ? [{ ...firstPhone, phone: seed.phone }, ...otherPhones]
        : phones,
    emails:
      firstEmail && !contact && seed.email
        ? [{ ...firstEmail, email: seed.email }, ...otherEmails]
        : emails,
  };
}

/** `2026-09-01` + `10:00` → an instant, or null when the date is missing. */
function instant(date: string, time: string): string | null {
  if (!date) return null;
  const parsed = new Date(`${date}T${time || "00:00"}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export function NuevoContacto({
  open,
  onClose,
  companyId,
  companyName,
  branches,
  contact,
  initialName,
  initialPhone,
  initialEmail,
  initialDataSource,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  companyId: number;
  companyName: string;
  /** The company's branches — a contact hangs off one, never off the company. */
  branches: Branch[];
  /** Pass to edit rather than create. */
  contact?: Contact;
  /** A new contact's name, where the host already heard it. */
  initialName?: string;
  /** The first phone row of a new contact, already filled in. */
  initialPhone?: string;
  /** The first email row of a new contact, already filled in. */
  initialEmail?: string;
  /** One of `DATA_SOURCES`: how a new contact reached us, where that is known. */
  initialDataSource?: string;
  /** Told what was created, for a host that carries on from it. */
  onCreated?: (contact: Contact) => void;
}) {
  const { t } = useTranslation();
  const seed: Seed = {
    name: initialName,
    phone: initialPhone,
    email: initialEmail,
    dataSource: initialDataSource,
  };
  const [draft, setDraft] = useState<Draft>(() => emptyDraft(branches, contact, seed));
  const [error, setError] = useState<string | null>(null);
  const [arcoOpen, setArcoOpen] = useState(false);
  const create = useCreateContact(companyId);
  const update = useUpdateContact(companyId);
  const isEdit = contact !== undefined;
  const pending = create.isPending || update.isPending;

  useEffect(() => {
    if (open) {
      setDraft(emptyDraft(branches, contact, seed));
      setError(null);
      setArcoOpen(Boolean(contact?.objected_at || contact?.blocked_at));
    }
    // `branches` is a fresh array on every render of the parent; keying the
    // reset on its length is enough and does not thrash the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact, branches.length]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((previous) => ({ ...previous, [key]: value }));
  }

  async function save() {
    if (!draft.name.trim()) {
      setError(t("newContact.nameRequired"));
      return;
    }
    if (!draft.branch) {
      setError(t("newContact.noBranch"));
      return;
    }
    setError(null);

    const answered = Object.fromEntries(
      CONSENTS.map(({ key }) => [key, ANSWER_TO_BOOLEAN[draft.consents[key]]]),
    ) as NonNullable<NewContact["consents"]>;

    const body: NewContact = {
      branch: Number(draft.branch),
      name: draft.name.trim(),
      job_title: draft.job_title.trim(),
      is_decision_maker: draft.is_decision_maker,
      // The cast is narrow and true by construction: the only writer of
      // `draft.data_source` is the select below, whose options are
      // `DATA_SOURCES`.
      data_source: draft.data_source
        ? (Number(draft.data_source) as NonNullable<NewContact["data_source"]>)
        : null,
      privacy_notice_delivered_at: instant(draft.notice_date, draft.notice_time),
      privacy_notice_version: draft.notice_version,
      objected_at: instant(draft.objected_at, ""),
      blocked_at: instant(draft.blocked_at, ""),
      phones: phonesBody(draft.phones),
      emails: emailsBody(draft.emails),
      // Only on create. Editing does not re-offer them, so nothing to send.
      ...(isEdit ? {} : { consents: answered }),
    };

    try {
      if (isEdit) {
        await update.mutateAsync({ id: contact.id, body });
        onClose();
        return;
      }
      const saved = await create.mutateAsync(body);
      onClose();
      onCreated?.(saved);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t("errors.saveContact"));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      kicker={companyName}
      title={t(isEdit ? "newContact.editTitle" : "newContact.title")}
      footer={
        <>
          <Button variant="primary" disabled={pending} onClick={() => void save()}>
            {pending ? t("newContact.saving") : t("newContact.save")}
          </Button>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </>
      }
    >
      {error ? (
        <p role="alert" className="mb-4 border-2 border-brand px-3 py-2 text-sm">
          {error}
        </p>
      ) : null}

      <div className="grid gap-5 sm:grid-cols-2 sm:gap-x-6">
        <FormField label={t("newContact.name")}>
          {(id) => (
            <Input
              id={id}
              value={draft.name}
              maxLength={200}
              data-autofocus=""
              placeholder={t("newContact.nameExample")}
              onChange={(event) => set("name", event.target.value)}
            />
          )}
        </FormField>

        <FormField label={t("newContact.jobTitle")}>
          {(id) => (
            <Input
              id={id}
              value={draft.job_title}
              maxLength={150}
              placeholder={t("newContact.jobTitleExample")}
              onChange={(event) => set("job_title", event.target.value)}
            />
          )}
        </FormField>

        <FormField label={t("newContact.branch")}>
          {(id) => (
            <Select
              id={id}
              value={draft.branch}
              onChange={(event) => set("branch", event.target.value)}
            >
              {branches.map((branch) => (
                <option key={branch.id} value={String(branch.id)}>
                  {branch.name}
                </option>
              ))}
            </Select>
          )}
        </FormField>

        <FormField
          label={t("newContact.dataSource")}
          hint={t("newContact.dataSourceHint")}
        >
          {(id) => (
            <Select
              id={id}
              value={draft.data_source}
              onChange={(event) => set("data_source", event.target.value)}
            >
              <option value="">{t("newContact.noDataSource")}</option>
              {DATA_SOURCES.map((value) => (
                <option key={value} value={value}>
                  {t(`dataSource.${value}`)}
                </option>
              ))}
            </Select>
          )}
        </FormField>

        <div className="sm:col-span-2">
          <label className="flex cursor-pointer items-center gap-2.5 text-sm">
            <input
              type="checkbox"
              className="h-[18px] w-[18px] accent-brand"
              checked={draft.is_decision_maker}
              onChange={(event) => set("is_decision_maker", event.target.checked)}
            />
            {t("newContact.decisionMaker")}
          </label>
          <p className="mt-1.5 text-[11px] text-mute-600">
            {t("newContact.decisionMakerHint")}
          </p>
        </div>
      </div>

      <section className="mt-6 border-t-2 border-rule pt-5">
        <h4 className="mb-1 text-[17px]">{t("newContact.channelsTitle")}</h4>
        <p className="mb-4 text-[13px] text-mute-600">{t("newContact.channelsLead")}</p>
        <div className="flex flex-col gap-5">
          <PhoneList rows={draft.phones} onChange={(rows) => set("phones", rows)} />
          <EmailList rows={draft.emails} onChange={(rows) => set("emails", rows)} />
        </div>
      </section>

      <section className="mt-6 border-t-2 border-rule pt-5">
        <h4 className="mb-1 text-[17px]">{t("newContact.noticeTitle")}</h4>
        <p className="mb-4 text-[13px] text-mute-600">{t("newContact.noticeLead")}</p>
        <div className="grid gap-5 sm:grid-cols-3 sm:gap-x-6">
          <FormField label={t("newContact.noticeDate")}>
            {(id) => (
              <Input
                id={id}
                type="date"
                value={draft.notice_date}
                onChange={(event) => set("notice_date", event.target.value)}
              />
            )}
          </FormField>
          <FormField label={t("newContact.noticeTime")}>
            {(id) => (
              <Input
                id={id}
                type="time"
                value={draft.notice_time}
                onChange={(event) => set("notice_time", event.target.value)}
              />
            )}
          </FormField>
          <FormField label={t("newContact.noticeVersion")}>
            {(id) => (
              <Select
                id={id}
                value={draft.notice_version}
                onChange={(event) => set("notice_version", event.target.value)}
              >
                <option value="">{t("newContact.noticeNotDelivered")}</option>
                {NOTICE_VERSIONS.map((version) => (
                  <option key={version} value={version}>
                    {version}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        </div>
      </section>

      <section className="mt-6 border-t-2 border-rule pt-5">
        <div className="mb-1 flex flex-wrap items-baseline justify-between gap-4">
          <h4 className="text-[17px]">{t("newContact.consentsTitle")}</h4>
          <span className="text-xs text-mute-600">{t("newContact.consentsAside")}</span>
        </div>
        <p className="mb-4 text-[13px] text-mute-600">{t("newContact.consentsLead")}</p>

        {isEdit ? (
          // Not re-offered on an edit, and not an oversight. A consent already
          // recorded has a `ConsentEvent` behind it; re-answering it from a
          // form would write a second position with weaker evidence than the
          // first, and the register would no longer say what actually happened
          // (.docs/06 §2.1).
          <p className="border-2 border-dashed border-mute-400 bg-panel px-4 py-3 text-[13px] text-mute-600">
            {t("newContact.consentsLocked")}
          </p>
        ) : (
          <div className="flex flex-col">
            {CONSENTS.map(({ key, label, note }) => (
              <div
                key={key}
                className="grid items-center gap-4 border-t border-rule py-3.5 sm:grid-cols-[1fr_auto]"
              >
                <div>
                  <p className="text-sm font-semibold">{t(`newContact.${label}`)}</p>
                  <p className="text-xs text-mute-600">{t(`newContact.${note}`)}</p>
                </div>
                <Seg<Answer>
                  name={`consent-${key}`}
                  value={draft.consents[key]}
                  onChange={(value) =>
                    set("consents", { ...draft.consents, [key]: value })
                  }
                  options={[
                    { value: "yes", label: t("newContact.yes") },
                    { value: "no", label: t("newContact.no") },
                    { value: "unasked", label: t("newContact.unasked") },
                  ]}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-6 border-t-2 border-rule pt-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h4 className="mb-0.5 text-[17px]">{t("newContact.arcoTitle")}</h4>
            <p className="text-[13px] text-mute-600">{t("newContact.arcoLead")}</p>
          </div>
          <Button
            aria-expanded={arcoOpen}
            onClick={() => setArcoOpen((previous) => !previous)}
          >
            {t(arcoOpen ? "newContact.arcoHide" : "newContact.arcoShow")}
          </Button>
        </div>
        {arcoOpen ? (
          <div className="mt-4 grid gap-5 sm:grid-cols-2 sm:gap-x-6">
            <FormField
              label={t("newContact.objectedAt")}
              hint={t("newContact.objectedHint")}
            >
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={draft.objected_at}
                  onChange={(event) => set("objected_at", event.target.value)}
                />
              )}
            </FormField>
            <FormField
              label={t("newContact.blockedAt")}
              hint={t("newContact.blockedHint")}
            >
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={draft.blocked_at}
                  onChange={(event) => set("blocked_at", event.target.value)}
                />
              )}
            </FormField>
          </div>
        ) : null}
      </section>
    </Dialog>
  );
}
